// src/components/mdx/client/visualizations/CreativeFatigueDecisionTree.client.tsx

/**
 * Creative Fatigue Decision Tree Component
 *
 * A React Flow based decision tree visualization that helps users determine if their creative
 * content is experiencing fatigue and provides recommended actions based on performance metrics.
 * The tree guides users through a series of questions and decisions, with visual feedback and
 * clear pathways for different scenarios.
 *
 * @module CreativeFatigueDecisionTree
 */

"use client"

import {
  HelpCircle,
  BarChart2,
  CheckCircle,
  XCircle,
  Zap,
  RefreshCw,
  Sparkles,
  RotateCcw,
  AlertCircle
} from "lucide-react"
import { cn } from "~/lib/utils"
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  Position,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  Handle,
  BackgroundVariant
} from '~/lib/charts/client'
import type {
  Node,
  NodeTypes,
  ReactFlowEdge
} from '~/lib/charts/client'
import { type CSSProperties, type FC, type ReactElement, cloneElement, useEffect } from "react"
import { useTheme } from "next-themes"

/**
 * Dynamically loads a CSS stylesheet at runtime, only when the component is mounted.
 * This prevents the CSS from being included in the global Next.js CSS pipeline,
 * which would otherwise bloat the global CSS bundle and slow down builds.
 *
 * @param href - The URL or path to the CSS file to load.
 * @example
 *   useRuntimeCss('https://unpkg.com/@xyflow/react/dist/style.css')
 */
function useRuntimeCss(href: string): void {
  useEffect(() => {
    // Prevent duplicate insertion of the same stylesheet
    if (typeof document === "undefined") return
    if (document.querySelector(`link[data-lib-style="${href}"]`)) return

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    link.setAttribute('data-lib-style', href)
    document.head.appendChild(link)

    // Optional: Remove stylesheet on unmount (uncomment if desired)
    return () => { link.remove() }
  }, [href])
}

/**
 * Represents an item within a decision node, containing an icon and descriptive text
 *
 * @interface NodeItem
 * @property {ReactElement<{ size?: number | string }>} icon - The icon component to display
 * @property {string} text - The descriptive text for this item
 */
export interface NodeItem {
  // React 19 narrowed `ReactElement`'s default props type from `any` to
  // `unknown`, so a bare `ReactElement` no longer lets `cloneElement` pass a
  // `size` prop. These are lucide icons that accept `size`, so type the
  // element's props to match what we clone in.
  icon: ReactElement<{ size?: number | string }>
  text: string
}

/**
 * Configuration data for a decision card node in the flow chart
 *
 * @interface DecisionCardData
 * @extends {Record<string, unknown>}
 * @property {string} title - The title text for the decision card
 * @property {string} [className] - Optional CSS classes to apply to the card
 * @property {number} [width] - Optional width override for the card
 * @property {NodeItem[]} items - Array of items to display in the card
 * @property {Object} [connections] - Optional connection point configuration
 * @property {boolean} [connections.top] - Whether to show top connection point
 * @property {boolean} [connections.bottom] - Whether to show bottom connection point
 * @property {boolean} [connections.left] - Whether to show left connection point
 * @property {boolean} [connections.right] - Whether to show right connection point
 */
export interface DecisionCardData extends Record<string, unknown> {
  title: string
  className?: string
  width?: number
  items: NodeItem[]
  connections?: {
    top?: boolean
    bottom?: boolean
    left?: boolean
    right?: boolean
  }
}

// Optimized styles object to reduce CSS bundle size
const cardStyles = {
  base:
    "rounded-xl border p-5 shadow-sm bg-white text-slate-900 dark:bg-slate-900/95 dark:text-slate-100 dark:border-slate-600/80",
  pink: "bg-pink-50 border-pink-100 dark:bg-pink-950/40 dark:border-pink-800/60",
  green: "bg-green-50 border-green-100 dark:bg-emerald-950/35 dark:border-emerald-800/50",
  red: "bg-red-50 border-red-100 dark:bg-red-950/35 dark:border-red-800/50",
  blue: "bg-blue-50 border-blue-100 dark:bg-blue-950/40 dark:border-blue-800/60",
  purple: "bg-purple-50 border-purple-100 dark:bg-purple-950/40 dark:border-purple-800/60"
} as const

const handleStyle: CSSProperties = {
  background: '#555',
  width: 8,
  height: 8
} as const

/**
 * A React component that renders a decision card node in the flow chart.
 * Handles connection points (handles) for React Flow, and renders the card content.
 *
 * @component
 * @param {Object} props - Component props
 * @param {DecisionCardData} props.data - The data configuration for this decision card
 * @returns {JSX.Element} The rendered decision card
 */
const DecisionCard: React.FC<{ data: DecisionCardData }> = ({ data }) => {
  // Default connection points if not specified
  const connections = data.connections ?? {
    top: true,
    bottom: true,
    left: false,
    right: false
  }

  return (
    <div
      className={cn(
        cardStyles.base,
        data.className
      )}
      style={{
        width: data.width ?? 280,
        maxWidth: "100%"
      }}
    >
      {/* Render connection handles for React Flow */}
      {connections.top && (
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          style={handleStyle}
        />
      )}

      {connections.bottom && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          style={handleStyle}
        />
      )}

      {connections.right && (
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={handleStyle}
        />
      )}

      {connections.left && (
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          style={handleStyle}
        />
      )}

      <h3 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-50">{data.title}</h3>
      <ul className="space-y-2.5">
        {data.items.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="flex-shrink-0 mt-0.5">
              {/* Clone the icon to enforce consistent sizing */}
              {cloneElement(item.icon, { size: 18 })}
            </span>
            <span className="text-base leading-tight text-slate-700 dark:text-slate-200">
              {item.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Node type definitions mapping for React Flow
 * 
 * @constant
 * @type {NodeTypes}
 */
const nodeTypes: NodeTypes = {
  decisionCard: DecisionCard
}

/**
 * Configuration interface for flow chart edges/connections
 * 
 * @interface EdgeConfig
 * @extends {ReactFlowEdge}
 * @property {string} id - Unique identifier for the edge
 * @property {string} source - ID of the source node
 * @property {string} target - ID of the target node
 * @property {string} sourceHandle - Connection point on source node
 * @property {string} targetHandle - Connection point on target node
 * @property {string} [type] - Optional edge type
 * @property {boolean} [animated] - Whether the edge should be animated
 * @property {Object} [markerEnd] - Arrow marker configuration
 * @property {React.CSSProperties} [style] - Custom CSS styles
 */
export interface EdgeConfig extends ReactFlowEdge {
  id: string
  source: string
  target: string
  sourceHandle: string
  targetHandle: string
  type?: string
  animated?: boolean
  markerEnd?: {
    type: MarkerType
    width: number
    height: number
    color: string
  }
  style?: React.CSSProperties
}

// Optimized constants to reduce memory allocation and ensure consistent edge styling
const MARKER_CONFIG = {
  type: MarkerType.ArrowClosed,
  width: 10,
  height: 10,
  color: '#888'
} as const

const EDGE_STYLE: CSSProperties = {
  stroke: '#888',
  strokeWidth: 2
} as const

const DASHED_EDGE_STYLE: CSSProperties = {
  stroke: '#888',
  strokeWidth: 2,
  strokeDasharray: '5,5'
} as const

/**
 * Inner component that implements the decision tree flow chart.
 * Handles all layout, node, and edge definitions.
 *
 * @component
 * @returns {JSX.Element} The rendered flow chart
 */
const DecisionFlowInner: React.FC = () => {
  const { resolvedTheme } = useTheme()
  const flowThemeClass = resolvedTheme === "dark" ? "react-flow dark" : "react-flow"

  // Layout constants for node positioning
  const centerX = 500 // Center position for the diagram
  const leftColumnX = centerX - 200 // Reduced horizontal spacing
  const rightColumnX = centerX + 200
  const subLeftX = leftColumnX - 120
  const subRightX = leftColumnX + 120

  // Vertical spacing for each level of the tree
  const rootY = 0
  const level2Y = rootY + 220 // Increased spacing for larger cards
  const level3Y = level2Y + 180 // Adjusted spacing for readability
  const level4Y = level3Y + 260 // Maintained spacing for readability
  const level5Y = level4Y + 220 // Adjusted spacing for larger cards
  
  // Card widths for different node types
  const questionWidth = 340
  const optionWidth = 220
  const solutionWidth = 300

  // Define the nodes for the decision tree
  const initialNodes: Node[] = [
    // Root question node
    {
      id: 'root',
      type: 'decisionCard',
      position: { x: centerX - questionWidth / 2, y: rootY },
      data: {
        title: 'Is your creative fatiguing?',
        className: cardStyles.pink,
        width: questionWidth,
        items: [
          {
            icon: <HelpCircle className="text-gray-600" />,
            text: 'Consider engagement metrics, conversion rates, and ad frequency'
          },
          {
            icon: <BarChart2 className="text-gray-600" />,
            text: 'Monitor changes over time rather than single data points'
          }
        ],
        connections: {
          top: true,
          bottom: true,
          left: false,
          right: false
        }
      }
    },

    // YES option node
    {
      id: 'yes',
      type: 'decisionCard',
      position: { x: leftColumnX - optionWidth / 2, y: level2Y },
      data: {
        title: 'YES',
        className: cardStyles.green,
        width: optionWidth,
        items: [
          {
            icon: <XCircle className="text-red-500" />,
            text: 'Creative is showing signs of fatigue'
          }
        ],
        connections: {
          top: true,
          bottom: true,
          left: false,
          right: false
        }
      }
    },

    // NO option node
    {
      id: 'no',
      type: 'decisionCard',
      position: { x: rightColumnX - optionWidth / 2, y: level2Y },
      data: {
        title: 'NO',
        className: cardStyles.red,
        width: optionWidth,
        items: [
          {
            icon: <CheckCircle className="text-green-500" />,
            text: 'Creative is performing well'
          }
        ],
        connections: {
          top: true,
          bottom: true,
          left: false,
          right: false
        }
      }
    },

    // Outperform question node (after YES)
    {
      id: 'outperform',
      type: 'decisionCard',
      position: { x: leftColumnX - questionWidth / 2, y: level3Y },
      data: {
        title: 'Does it still outperform ad set averages?',
        className: cardStyles.blue,
        width: questionWidth,
        items: [
          {
            icon: <HelpCircle className="text-gray-600" />,
            text: 'Compare with other creatives in the same ad set'
          },
          {
            icon: <BarChart2 className="text-gray-600" />,
            text: 'Look at key metrics like ROAS and CPA'
          }
        ],
        connections: {
          top: true,
          bottom: true,
          left: false,
          right: false
        }
      }
    },

    // Sub-YES option node
    {
      id: 'sub-yes',
      type: 'decisionCard',
      position: { x: subRightX - optionWidth / 2, y: level4Y },
      data: {
        title: 'YES',
        className: cardStyles.green,
        width: optionWidth,
        items: [
          {
            icon: <CheckCircle className="text-green-500" />,
            text: 'Still delivers good results despite fatigue'
          }
        ],
        connections: {
          top: true,
          bottom: true,
          left: false,
          right: false
        }
      }
    },

    // Sub-NO option node
    {
      id: 'sub-no',
      type: 'decisionCard',
      position: { x: subLeftX - optionWidth / 2, y: level4Y },
      data: {
        title: 'NO',
        className: cardStyles.red,
        width: optionWidth,
        items: [
          {
            icon: <XCircle className="text-red-500" />,
            text: 'Performance has dropped below acceptable levels'
          }
        ],
        connections: {
          top: true,
          bottom: true,
          left: false,
          right: false
        }
      }
    },

    // Continue running solution node (after sub-YES)
    {
      id: 'continue-yes',
      type: 'decisionCard',
      position: { x: centerX - solutionWidth / 2, y: level5Y },
      data: {
        title: 'Continue running while testing alternatives',
        className: cardStyles.blue,
        width: solutionWidth,
        items: [
          {
            icon: <Zap className="text-blue-500" />,
            text: 'Keep the creative active while it performs'
          },
          {
            icon: <Sparkles className="text-blue-500" />,
            text: 'Develop new variations based on winning elements'
          },
          {
            icon: <RefreshCw className="text-blue-500" />,
            text: 'Continue monitoring performance regularly'
          }
        ],
        connections: {
          top: true,
          bottom: false,
          left: false,
          right: true
        }
      }
    },

    // Phase out solution node (after sub-NO)
    {
      id: 'phase-out',
      type: 'decisionCard',
      position: { x: subLeftX - solutionWidth / 2, y: level5Y },
      data: {
        title: 'Phase out gradually while introducing new creatives',
        className: cardStyles.purple,
        width: solutionWidth,
        items: [
          {
            icon: <RotateCcw className="text-purple-500" />,
            text: 'Gradually reduce budget allocation'
          },
          {
            icon: <Sparkles className="text-purple-500" />,
            text: 'Introduce fresh creatives with new messaging angles'
          },
          {
            icon: <AlertCircle className="text-purple-500" />,
            text: 'Analyze what caused fatigue to improve future designs'
          }
        ],
        connections: {
          top: true,
          bottom: false,
          left: true,
          right: false
        }
      }
    }
  ]

  // Define the edges (connections) between nodes
  const initialEdges: EdgeConfig[] = [
    // Root to YES
    {
      id: 'root-yes',
      source: 'root',
      target: 'yes',
      sourceHandle: 'bottom',
      targetHandle: 'top',
      type: 'step',
      markerEnd: MARKER_CONFIG,
      style: EDGE_STYLE
    },

    // Root to NO
    {
      id: 'root-no',
      source: 'root',
      target: 'no',
      sourceHandle: 'bottom',
      targetHandle: 'top',
      type: 'step',
      markerEnd: MARKER_CONFIG,
      style: EDGE_STYLE
    },

    // YES to Outperform
    {
      id: 'yes-outperform',
      source: 'yes',
      target: 'outperform',
      sourceHandle: 'bottom',
      targetHandle: 'top',
      type: 'step',
      markerEnd: MARKER_CONFIG,
      style: EDGE_STYLE
    },

    // NO to Continue-Yes (linking directly to the bottom card)
    {
      id: 'no-continue',
      source: 'no',
      target: 'continue-yes',
      sourceHandle: 'bottom',
      targetHandle: 'top',
      type: 'step',
      markerEnd: MARKER_CONFIG,
      style: EDGE_STYLE
    },

    // Outperform to Sub-YES
    {
      id: 'outperform-yes',
      source: 'outperform',
      target: 'sub-yes',
      sourceHandle: 'bottom',
      targetHandle: 'top',
      type: 'step',
      markerEnd: MARKER_CONFIG,
      style: EDGE_STYLE
    },

    // Outperform to Sub-NO
    {
      id: 'outperform-no',
      source: 'outperform',
      target: 'sub-no',
      sourceHandle: 'bottom',
      targetHandle: 'top',
      type: 'step',
      markerEnd: MARKER_CONFIG,
      style: EDGE_STYLE
    },

    // Sub-YES to Continue
    {
      id: 'sub-yes-continue',
      source: 'sub-yes',
      target: 'continue-yes',
      sourceHandle: 'bottom',
      targetHandle: 'top',
      type: 'step',
      markerEnd: MARKER_CONFIG,
      style: EDGE_STYLE
    },

    // Sub-NO to Phase Out
    {
      id: 'sub-no-phase',
      source: 'sub-no',
      target: 'phase-out',
      sourceHandle: 'bottom',
      targetHandle: 'top',
      type: 'step',
      markerEnd: MARKER_CONFIG,
      style: EDGE_STYLE
    },

    // Continue-yes loop connection (goes back to root)
    {
      id: 'continue-yes-loop',
      source: 'continue-yes',
      target: 'root',
      sourceHandle: 'right',
      targetHandle: 'top',
      type: 'step',
      animated: true,
      style: DASHED_EDGE_STYLE
    },

    // Phase-Out loop connection (goes back to root)
    {
      id: 'phase-out-loop',
      source: 'phase-out',
      target: 'root',
      sourceHandle: 'left',
      targetHandle: 'top',
      type: 'step',
      animated: true,
      style: DASHED_EDGE_STYLE
    }
  ]

  // React Flow state hooks for nodes and edges
  const [nodes, _setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, _setEdges, onEdgesChange] = useEdgesState(initialEdges)

  return (
    <div style={{ width: '100%', height: '850px' }}>
      <ReactFlow
        className={flowThemeClass}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{
          padding: 0.2,
          includeHiddenNodes: true
        }}
        minZoom={0.4}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 0.90 }}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
      >
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        <Controls position="bottom-right" showInteractive={false} />
      </ReactFlow>
    </div>
  )
}

/**
 * Root component that wraps the flow chart with React Flow provider.
 * Dynamically loads the xyflow CSS at runtime only when this component is mounted,
 * preventing global CSS bundle bloat and improving build performance.
 *
 * @component
 * @returns {JSX.Element} The wrapped flow chart component
 * @example
 * // Basic usage
 * <CreativeFatigueDecisionTree />
 */
const XYFLOW_CSS_URL = "https://unpkg.com/@xyflow/react@12.8.6/dist/style.css" // Use specific version for stability

const CreativeFatigueDecisionTree: FC = () => {
  // Load the xyflow CSS only when this component is mounted (client-side only)
  useRuntimeCss(XYFLOW_CSS_URL)

  return (
    <ReactFlowProvider>
      <DecisionFlowInner />
    </ReactFlowProvider>
  )
}

export default CreativeFatigueDecisionTree